import { Editor, Node, Transforms } from 'slate';
import type { AutoFormatRule } from '../plugins/withAutoFormat';
import { BulletedListSymbol, NumberedListSymbol, isListItemNode, type ListEditor } from '../plugins/withLists';

/**
 * Characters that indicate the next quote typed should be an opening
 * (left) quote. When the character immediately before the cursor is one
 * of these, the quote is treated as opening; otherwise it is closing.
 *
 * Includes empty string (start of node), whitespace, newline, and both
 * opening and closing curly quotes (to handle quoted-within-quoted text).
 */
const OpeningQuoteLeadingCharacters = ['', ' ', '\n', '\u201C', '\u201D', '\u2018', '\u2019'];

/**
 * Maps straight quote characters to their curly opening/closing pairs.
 *
 * - `"` → `["\u201C", "\u201D"]` (left/right double quotation marks)
 * - `'` → `["\u2018", "\u2019"]` (left/right single quotation marks)
 */
const SmartQuoteMap: Record<string, [string, string]> = {
  '"': ['\u201C', '\u201D'],
  "'": ['\u2018', '\u2019'],
};

/**
 * Inserts a directional (curly) quote based on the character preceding the
 * cursor. Uses {@link OpeningQuoteLeadingCharacters} to determine whether
 * an opening or closing quote should be inserted.
 *
 * Designed to be called from a `text` mode autoformat rule's `format`
 * function.
 */
const insertSmartQuote = (editor: Editor, straightQuote: string): void => {
  const replacement = SmartQuoteMap[straightQuote];

  if (!replacement || !editor.selection) {
    return;
  }

  let isOpening = true;
  const node = Node.get(editor, editor.selection.anchor.path);

  if (node !== null) {
    const text = Node.string(node);
    const characterBefore = text.charAt(editor.selection.anchor.offset - 1);
    isOpening = OpeningQuoteLeadingCharacters.includes(characterBefore);
  }

  Transforms.insertText(editor, isOpening ? replacement[0] : replacement[1]);
};

/**
 * Autoformat rules that convert straight quotes into typographic
 * (curly/smart) quotes.
 *
 * Matches straight quotes as well as macOS-preconverted curly quotes to
 * ensure correct behavior regardless of OS-level smart quote settings.
 *
 * @example
 * ```
 * "hello"  →  \u201Chello\u201D
 * 'world'  →  \u2018world\u2019
 * ```
 */
export const AutoFormatSmartQuoteRules: Array<AutoFormatRule> = [
  {
    match: ['"', '\u201C', '\u201D'],
    mode: 'text',
    format: (editor) => insertSmartQuote(editor, '"'),
  },
  {
    match: ["'", '\u2018', '\u2019'],
    mode: 'text',
    format: (editor) => insertSmartQuote(editor, "'"),
  },
];

/**
 * Autoformat rules that convert markdown-style list shortcuts into list
 * blocks.
 *
 * - `- ` (dash + space) → bulleted list (requires `withLists` plugin with
 *   {@link BulletedListSymbol}).
 * - `1. ` (number + period + space) → numbered list (requires `withLists`
 *   plugin with {@link NumberedListSymbol}).
 *
 * Both rules guard against triggering inside existing list items.
 */
export const AutoFormatListRules: Array<AutoFormatRule> = [
  {
    match: '- ',
    mode: 'block',
    query: (editor) => {
      const [match] = Editor.nodes(editor, { match: isListItemNode });
      return !match;
    },
    format: (editor) => {
      if (BulletedListSymbol in editor && editor[BulletedListSymbol] === true) {
        (editor as Editor & ListEditor).toggleList('bulleted-list');
      }
    },
  },
  {
    match: String.raw`^\d+\.$ `,
    matchByRegex: true,
    mode: 'block',
    query: (editor) => {
      const [match] = Editor.nodes(editor, { match: isListItemNode });
      return !match;
    },
    format: (editor) => {
      if (NumberedListSymbol in editor && editor[NumberedListSymbol] === true) {
        (editor as Editor & ListEditor).toggleList('numbered-list');
      }
    },
  },
];
